import { DeploymentService } from "@packages/client";
import { fromUnixTime } from "date-fns";

import {
  InstanceData,
  ResourceOverviewData
} from "Api/observability/pipeline/types";

import { MetricsUnit2 } from "./type";

const units = ["cpu", "memory", "/mnt", "/tmp"] as const;

export const metricsByServiceName = (
  metrics: ResourceOverviewData[] = [],
  appNameFilters?: string[]
) => {
  const metricsByUnit: Record<string, MetricsUnit2> = {};
  const serviceNames = new Set<string>();

  const unitValueFunc = {
    cpu: s => s.cpu_used?.avg || 0,
    memory: s => s.memory_used?.avg || 0,
    "/tmp": s => s.mountpoints?.["/tmp"]?.disk_used.avg || 0,
    "/mnt": s => s.mountpoints?.["/mnt"]?.disk_used.avg || 0
  };

  const maxValueFunc = {
    cpu: s => s.cpu_limit?.max || 0,
    memory: s => s.memory_limit?.max || 0,
    "/tmp": s => s.mountpoints["/tmp"]?.disk_limit?.max || 0,
    "/mnt": s => s.mountpoints?.["/mnt"]?.disk_limit.max || 0
  };

  const addMetric = ({ unit, pointValues, date, data, sum }) => {
    if (!metricsByUnit[unit]) metricsByUnit[unit] = [];
    metricsByUnit[unit].push({ date, data, sum, ...pointValues });
  };

  for (const { timestamp, services } of metrics) {
    if (!services) continue;
    units.forEach(unit => {
      const data: Array<{ name: string; point: InstanceData[] }> = [];
      const sum = { value: 0, max: 0 };
      const pointValues = Object.entries(services).reduce((accum, next) => {
        const [name, service] = next;

        if (appNameFilters && !appNameFilters.includes(name)) return accum;

        const valueFunc = unitValueFunc[unit];
        const value = valueFunc(service);
        accum[name] = value;
        serviceNames.add(name);

        const maxFunc = maxValueFunc[unit];
        const max = maxFunc(service);

        sum.value += value;
        sum.max += max;
        data.push({ name, point: service });
        return accum;
      }, {});
      addMetric({
        unit,
        pointValues,
        date: fromUnixTime(timestamp),
        data,
        sum
      });
    });
  }
  return { serviceNames: Array.from(serviceNames), metricsByUnit };
};

export const generateServiceMeta = ({
  deployments,
  serviceNames = []
}: {
  deployments?: DeploymentService[];
  serviceNames?: string[];
}) => {
  if (serviceNames.length) {
    const router = {
      name: "router",
      kind: "service"
    } as unknown as DeploymentService;
    const map: Array<[string, DeploymentService]> = [];
    for (const name of serviceNames) {
      const service =
        name === "router"
          ? router
          : deployments?.find(s => name.startsWith(s.name));
      if (service) map.push([name, service]);
    }
    return new Map(map);
  }
  return new Map(deployments?.map(service => [service.name, service]));
};

export const metricsByServiceKind = (
  metricsUnit: Record<string, MetricsUnit2>,
  metaMap: Map<string, DeploymentService>
) => {
  const metricsUnitByKind = {};
  const addMetric = ({ unit, pointValues, date, data, sum }) => {
    if (!metricsUnitByKind[unit]) metricsUnitByKind[unit] = [];
    metricsUnitByKind[unit].push({ date, data, sum, ...pointValues });
  };
  Object.entries(metricsUnit).forEach(([unit, unitData]) => {
    unitData.forEach(d => {
      const pointValues = { webapp: 0, worker: 0, service: 0 };
      const { data, date, sum, ...others } = d;
      Object.entries(others).forEach(([name, value]) => {
        // Watch out for router adding values if we want to allow
        // router we need to make sure it is included in the metaMap
        const kind = metaMap.get(name)?.["kind"];
        pointValues[kind] += value;
      });
      addMetric({ unit, pointValues, date, data, sum });
    });
  });
  return metricsUnitByKind;
};
